/**
 * Cookie utilities for client-side cookie management
 * These functions interact with the API route to manage httpOnly cookies
 */

const COOKIES_API = "/api/auth/cookies";

export interface CookieData {
  accessToken: string | null;
  refreshToken: string | null;
  userData: unknown | null;
}

/**
 * Get cookies from server
 */
export async function getCookies(): Promise<CookieData> {
  try {
    const response = await fetch(COOKIES_API, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      return { accessToken: null, refreshToken: null, userData: null };
    }

    const result = await response.json();
    return (
      result.data || { accessToken: null, refreshToken: null, userData: null }
    );
  } catch (error) {
    console.error("Failed to get cookies:", error);
    return { accessToken: null, refreshToken: null, userData: null };
  }
}

/**
 * Set cookies on server
 */
export async function setCookies(data: {
  accessToken?: string;
  refreshToken?: string;
  userData?: unknown;
}): Promise<boolean> {
  try {
    const response = await fetch(COOKIES_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    return response.ok;
  } catch (error) {
    console.error("Failed to set cookies:", error);
    return false;
  }
}

/**
 * Clear all auth cookies
 */
export async function clearCookies(): Promise<boolean> {
  try {
    const response = await fetch(COOKIES_API, {
      method: "DELETE",
      credentials: "include",
    });

    return response.ok;
  } catch (error) {
    console.error("Failed to clear cookies:", error);
    return false;
  }
}

/**
 * Get access token from cookies (client-side)
 */
export async function getAccessToken(): Promise<string | null> {
  const cookies = await getCookies();
  return cookies.accessToken;
}

/**
 * Get refresh token from cookies (client-side)
 */
export async function getRefreshToken(): Promise<string | null> {
  const cookies = await getCookies();
  return cookies.refreshToken;
}

/**
 * Verify that cookies are stored correctly
 * Useful for debugging after registration/login
 */
export async function verifyCookies(): Promise<{
  success: boolean;
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
  hasUserData: boolean;
  details: {
    accessTokenLength: number;
    refreshTokenLength: number;
  };
}> {
  const cookies = await getCookies();
  return {
    success: !!(cookies.accessToken && cookies.refreshToken),
    hasAccessToken: !!cookies.accessToken,
    hasRefreshToken: !!cookies.refreshToken,
    hasUserData: !!cookies.userData,
    details: {
      accessTokenLength: cookies.accessToken?.length || 0,
      refreshTokenLength: cookies.refreshToken?.length || 0,
    },
  };
}
